import { CountBird } from '@league/ui';
import type { ScoreCardResponse } from '@league/shared/contract';
import { useQuery } from '@tanstack/react-query';

import { ApiError, apiFetch } from '../../lib/api';
import { TableScoreCard } from '../player/ScoreCard';

/**
 * WHERE THE QR ON A TABLE'S PRINTED SCORE SHEET LANDS — owner, 2026-10-06. Whoever is at the table
 * scans it and enters the table's scores, signed in or not. The address carries the sheet's key;
 * the API decides everything, and this page shows its answer.
 */
export const TABLE_SHEET_PATH = /^\/score\/([0-9a-f-]{36})\/(\d{1,2})\/([A-Za-z0-9_-]{10,64})\/?$/i;

export function tableSheetFrom(pathname: string): { sessionId: string; tableNumber: string; key: string } | null {
  const match = TABLE_SHEET_PATH.exec(pathname);
  return match ? { sessionId: match[1]!, tableNumber: match[2]!, key: match[3]! } : null;
}

export function TableSheetPage({ brand, sessionId, tableNumber, sheetKey }: {
  brand: string;
  sessionId: string;
  tableNumber: string;
  sheetKey: string;
}) {
  const endpoint = `/api/public/table-sheets/${sessionId}/${tableNumber}/${sheetKey}`;
  const queryKey = ['table-sheet', sessionId, tableNumber] as const;
  /* The same query the card runs; read here only to name the league and to say when it is not there. */
  const card = useQuery({ queryKey, queryFn: () => apiFetch<ScoreCardResponse>(endpoint), retry: false });
  const missing = card.error instanceof ApiError && card.error.status === 404;

  return (
    <div className="flex min-h-screen flex-col">
      <header className="border-b border-light bg-paper">
        <div className="mx-auto flex w-full max-w-2xl items-center gap-2 px-4 py-3">
          <CountBird size={72} ground="light" className="shrink-0" />
          <span className="font-display text-lg leading-none">{brand}</span>
        </div>
      </header>
      <main className="mx-auto w-full max-w-2xl flex-1 px-3 py-5 sm:px-5">
        {card.data ? (
          <p className="px-1 text-[15px] text-ink-2">
            {card.data.card.leagueName} &middot; Session {card.data.card.sessionNumber}
          </p>
        ) : null}
        {missing ? (
          <div className="mt-6 rounded-md border border-light bg-paper p-5">
            <h1 className="font-display text-[22px]">This score sheet isn’t open</h1>
            <p className="mt-2 text-[15px] text-ink-2">Ask your director.</p>
          </div>
        ) : (
          <TableScoreCard
            endpoint={endpoint}
            queryKey={queryKey}
            draftKey={`table-sheet-draft:${sessionId}:${tableNumber}`}
            draftBelongs={(base) => base.sessionId === sessionId && String(base.tableNumber) === tableNumber}
          />
        )}
      </main>
    </div>
  );
}
